/**
 * Station frame view-models (plan §6 M2-18): the header (station, faction, standing, wallet, lease, collections,
 * cargo, storage banner), the tabs the station offers, and the Launch button.
 */
import { holdReadout } from '../../career/hullFit'
import { cargoUsedT } from '../../career/ops'
import type { Collections } from '../../career/types'
import { stationDef, stationIndexOf } from '../../data/m2'
import type { M2Data, StationDef, StationService } from '../../data/m2Schemas'
import { STORAGE_BANNER } from '../../save/storage'
import { UI_CONFIG, type UiConfig } from '../config'
import { STATION_TABS, type StationTab, type UiState } from '../contracts'
import { formatCr, formatDueIn, formatStanding, formatTonnes } from '../format'
import { deskStaffLine } from './seeds'

export type Severity = 'ok' | 'warning' | 'danger'

export interface CollectionsView {
  state: Collections
  label: string
  severity: Severity
}

export interface StationHeaderModel {
  stationName: string
  factionName: string
  standing: number
  standingLabel: string
  wallet: string
  /** "180,000 cr", or "Owned" after ownership. */
  principal: string
  /** "in 23 min of flight", "due now", or "Paid off". */
  nextDue: string
  collections: CollectionsView
  /** Insurer debt after ownership, or null when there is none. */
  debt: string | null
  cargoLabel: string
  /** 0..1 of the hold in use. */
  cargoFraction: number
  /** "Progress may not persist in this browser mode." while saves fall back to localStorage (or have no storage). */
  storageBanner: string | null
  /** M7 S33 (C8): who is on the desks here ("Lease Office: Ines Duarte, registry clerk · Claim desk: …"), or null. */
  deskStaff: string | null
  /** M7 S33 (FR texture seed): the bay plate in words, or null. */
  plate: string | null
}

/** The tab a service unlocks (Saves is always there). */
const SERVICE_TAB: Readonly<Record<StationService, StationTab>> = {
  market: 'market',
  outfitting: 'outfitting',
  repair: 'repair',
  lease: 'lease',
  contracts: 'contracts',
}

/** The tabs `station` shows, in `STATION_TABS` order: one per service it offers, plus Saves. */
export function visibleTabs(station: Pick<StationDef, 'services'>): StationTab[] {
  return STATION_TABS.filter((tab) => tab === 'saves' || station.services.some((s) => SERVICE_TAB[s] === tab))
}

/** The tab to show: `tab` when the station offers it, else its first tab. */
export function effectiveTab(tabs: readonly StationTab[], tab: StationTab): StationTab {
  return tabs.includes(tab) ? tab : (tabs[0] ?? 'saves')
}

export function tabLabel(tab: StationTab, cfg: UiConfig = UI_CONFIG): string {
  return cfg.tabs[tab]
}

/** The docked station's def, or null when the state has none (or an unknown id). */
export function dockedStation(data: Pick<M2Data, 'stations'>, state: Pick<UiState, 'stationId'>): StationDef | null {
  if (state.stationId === null || stationIndexOf(data, state.stationId) < 0) return null
  return stationDef(data, state.stationId)
}

export function collectionsView(c: Collections, repoPaused: boolean): CollectionsView {
  switch (c) {
    case 'none':
      return { state: c, label: 'In good standing', severity: 'ok' }
    case 'notice':
      return { state: c, label: 'Collections notice', severity: 'warning' }
    case 'repo':
      return { state: c, label: repoPaused ? 'Repossession (paused)' : 'Repossession ordered', severity: 'danger' }
  }
}

export function stationHeader(data: M2Data, state: UiState, cfg: UiConfig = UI_CONFIG): StationHeaderModel | null {
  const career = state.career
  const station = dockedStation(data, state)
  if (career === null || station === null) return null
  const lease = career.lease
  const standing = career.standings[station.faction]
  const used = cargoUsedT(career)
  const cap = career.cargo.capacityT
  return {
    stationName: station.name,
    factionName: cfg.factions[station.faction],
    standing,
    standingLabel: formatStanding(standing),
    wallet: formatCr(career.wallet),
    principal: lease.owned ? 'Owned' : formatCr(lease.principal),
    nextDue: lease.owned ? 'Paid off' : formatDueIn(lease.nextDueAt, state.gameTime),
    collections: collectionsView(lease.collections, lease.repoPausedUntil > state.gameTime),
    debt: career.debt > 0 ? formatCr(career.debt) : null,
    cargoLabel: holdReadout(`${formatTonnes(used)} / ${formatTonnes(cap)} t`, career.ship.defId),
    cargoFraction: cap > 0 ? Math.min(1, used / cap) : 0,
    storageBanner: state.storage === 'indexeddb' ? null : STORAGE_BANNER,
    deskStaff: deskStaffLine(station.desk ?? null),
    plate: station.desk?.plate ?? null,
  }
}

export interface LaunchModel {
  enabled: boolean
  label: string
}

/** Shown while a save loads (the load hold): the Launch label and the station status line. */
export const LOADING_LABEL = 'Loading save…'

/** Launch waits for a loading save to dock, then for the deferred station and prop models ("Preparing launch… 64%"). */
export function launchModel(state: Pick<UiState, 'assetsReady' | 'assetsProgress'> & Partial<Pick<UiState, 'loading'>>): LaunchModel {
  if (state.loading === true) return { enabled: false, label: LOADING_LABEL }
  if (state.assetsReady) return { enabled: true, label: 'Launch' }
  const p = Math.max(0, Math.min(1, state.assetsProgress))
  return { enabled: false, label: `Preparing launch… ${Math.floor(p * 100)}%` }
}
